/**
 * Modale Rachat Intelligent — version batch.
 *
 * Workflow :
 *   1. Configuration du scope (pôle/niveau/option/classe, semestre, tranche moy,
 *      type de cas, plafond +N points, session ordinaire en dernier recours).
 *   2. Détecter → dry-run backend : rachetables (moyenne après = calcul officiel)
 *      et non-rachetables avec leur motif.
 *   3. Par étudiant : rachat intelligent (appliquer la proposition) ou rachat
 *      manuel (le jury ajuste les notes, simule, applique). En masse : les cochés,
 *      appliqués par paquets sous un même lot.
 *   4. Onglet Historique : liste des batches passés, détail + annulation.
 *
 * Règles (03/10/2026) : rattrapage d'abord, peu à peu sur plusieurs notes, UE
 * éliminatoires puis UE les plus faibles ; session ordinaire seulement sur
 * décision explicite, une fois les rattrapages épuisés.
 */
import React, { useMemo, useState } from 'react';
import {
  X, Loader, AlertTriangle, CheckCircle2, Filter, History, Info,
  Calculator, Trash2, ChevronRight, RefreshCw,
} from 'lucide-react';
import {
  useProposerBatchRachatMutation,
  useGetHistoriqueBatchesQuery,
  useAnnulerBatchRachatMutation,
  useLazyGetBatchDetailsQuery,
  useGetParametresRachatQuery,
  PropositionEtudiantBatch,
  NonRachetable,
} from '../../services/rachatApi';
import RachatManuelModal from './RachatManuelModal';
import {
  useGetPolesQuery, useGetOptionsQuery, useGetClassesQuery,
} from '../../services/polesOptionsApi';

interface Props {
  open: boolean;
  anneeAcademique: string;
  semestreDefaut: 1 | 2;
  scopeDefaut?: { pole?: string; niveau?: string; option?: string; classe?: string };
  onClose: () => void;
  onSuccess?: () => void;
}

const NIVEAUX = ['L1', 'L2', 'L3', 'M1', 'M2'];
const TAILLE_PAQUET = 15;

const LIB_CAS: Record<string, string> = {
  sous_admission: 'Sous adm.',
  eliminatoire: 'Élim.',
  mixte: 'Mixte',
  manuel: 'Manuel',
};

const RachatIntelligentBatchModal: React.FC<Props> = ({
  open, anneeAcademique, semestreDefaut, scopeDefaut, onClose, onSuccess,
}) => {
  const [tab, setTab] = useState<'config' | 'historique'>('config');

  // Scope
  const [semestre, setSemestre] = useState<1 | 2>(semestreDefaut);
  const [pole, setPole] = useState(scopeDefaut?.pole || '');
  const [niveau, setNiveau] = useState(scopeDefaut?.niveau || '');
  const [option, setOption] = useState(scopeDefaut?.option || '');
  const [classe, setClasse] = useState(scopeDefaut?.classe || '');
  const [moyMin, setMoyMin] = useState('');
  const [moyMax, setMoyMax] = useState('');
  const [casFiltre, setCasFiltre] = useState<'tous' | 'sous_admission' | 'eliminatoire' | 'mixte'>('tous');
  const [plafond, setPlafond] = useState('4.0');
  const [propagerAuxDevoirs, setPropagerAuxDevoirs] = useState(false);
  // Session ordinaire : jamais par défaut ; dernier recours sur décision du jury
  const [autoriserSo, setAutoriserSo] = useState(false);
  // SO à 0 : réputée non-participation, exclue sauf décision explicite
  const [autoriserSoZero, setAutoriserSoZero] = useState(false);
  // Rachat manuel ouvert pour cet étudiant
  const [manuelPour, setManuelPour] = useState<number | null>(null);
  // Progression de l'application par paquets
  const [progression, setProgression] = useState<{ fait: number; total: number } | null>(null);

  const { data: poles } = useGetPolesQuery({ actif: true });
  const { data: options } = useGetOptionsQuery({ actif: true });
  const { data: classes } = useGetClassesQuery({ actif: true });
  const { data: params } = useGetParametresRachatQuery();

  const optionsFiltrees = useMemo(() => {
    const all = (options || []) as any[];
    if (!pole) return all;
    const p = (poles || []).find((x: any) => x.nom === pole);
    return p ? all.filter((o: any) => o.pole === p.id) : all;
  }, [options, poles, pole]);

  const [proposer, { isLoading }] = useProposerBatchRachatMutation();
  const [preview, setPreview] = useState<any | null>(null);
  const [applied, setApplied] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmer, setConfirmer] = useState(false);

  // Filtres d'affichage des résultats
  const [filtreCas, setFiltreCas] = useState<'' | 'sous_admission' | 'eliminatoire' | 'mixte'>('');
  const [filtreGain, setFiltreGain] = useState(false);
  // Sélection manuelle des étudiants à racheter (par défaut : tous cochés)
  const [selection, setSelection] = useState<Set<number>>(new Set());

  const scopeActif = !!(pole || niveau || option || classe);

  const payloadBase = () => ({
    annee_academique: anneeAcademique,
    semestre,
    pole: pole || undefined,
    niveau: niveau || undefined,
    option: option || undefined,
    classe: classe || undefined,
    moy_min: moyMin ? parseFloat(moyMin.replace(',', '.')) : null,
    moy_max: moyMax ? parseFloat(moyMax.replace(',', '.')) : null,
    cas_filtre: casFiltre,
    plafond_augmentation: parseFloat(plafond),
    propager_aux_devoirs: propagerAuxDevoirs,
    autoriser_so: autoriserSo,
    autoriser_so_zero: autoriserSo && autoriserSoZero,
  });

  const handlePreview = async () => {
    if (!scopeActif) {
      setError('Sélectionnez au moins un filtre de scope (pôle, niveau, option ou classe).');
      return;
    }
    setError(null); setApplied(null); setConfirmer(false);
    try {
      const res = await proposer({ ...payloadBase(), dry_run: true }).unwrap();
      setPreview(res);
      // Tous cochés par défaut
      setSelection(new Set((res.propositions || []).map((p: any) => p.etudiant_id)));
    } catch (e: any) {
      setError(e?.data?.error || e?.message || 'Erreur de prévisualisation');
    }
  };

  // Application par paquets (15 étudiants) sous un même lot : chaque étudiant
  // est re-proposé, vérifié et écrit côté serveur ; un échec n'annule que lui.
  const appliquerIds = async (ids: number[]) => {
    setError(null);
    const cumul: any = {
      nb_etudiants_traites: 0, nb_notes_examen_modifiees: 0, nb_notes_devoir_modifiees: 0,
      appliques: [], echecs: [], batch_id: null,
    };
    setProgression({ fait: 0, total: ids.length });
    try {
      for (let i = 0; i < ids.length; i += TAILLE_PAQUET) {
        const paquet = ids.slice(i, i + TAILLE_PAQUET);
        const res = await proposer({
          ...payloadBase(), etudiant_ids: paquet, batch_id: cumul.batch_id, dry_run: false,
        }).unwrap();
        cumul.batch_id = cumul.batch_id || res.batch_id;
        cumul.nb_etudiants_traites += res.nb_etudiants_traites;
        cumul.nb_notes_examen_modifiees += res.nb_notes_examen_modifiees;
        cumul.nb_notes_devoir_modifiees += res.nb_notes_devoir_modifiees;
        cumul.appliques.push(...(res.appliques || []));
        cumul.echecs.push(...(res.echecs || []));
        setProgression({ fait: Math.min(i + TAILLE_PAQUET, ids.length), total: ids.length });
      }
    } catch (e: any) {
      setError((e?.data?.error || e?.message || "Erreur lors de l'application")
        + (cumul.nb_etudiants_traites ? ` — ${cumul.nb_etudiants_traites} étudiant(s) déjà rachetés avant l'erreur (lot ${cumul.batch_id}).` : ''));
    } finally {
      setProgression(null);
    }
    if (cumul.nb_etudiants_traites || cumul.echecs.length) {
      setApplied(cumul);
      retirerDeLaListe(cumul.appliques.map((a: any) => a.etudiant_id));
      setConfirmer(false);
      if (onSuccess) onSuccess();
    }
  };

  // Les étudiants rachetés quittent la liste : le jury continue avec les autres.
  const retirerDeLaListe = (ids: number[]) => {
    if (!ids.length) return;
    const retires = new Set(ids);
    setPreview((p: any) => p && {
      ...p,
      propositions: (p.propositions || []).filter((x: any) => !retires.has(x.etudiant_id)),
      non_rachetables: (p.non_rachetables || []).filter((x: any) => !retires.has(x.etudiant_id)),
      total_eligibles: (p.propositions || []).filter((x: any) => !retires.has(x.etudiant_id)).length,
    });
    setSelection((sel) => {
      const next = new Set(sel);
      ids.forEach((i) => next.delete(i));
      return next;
    });
  };

  const handleAppliquer = () => appliquerIds(Array.from(selection));

  const propositions: PropositionEtudiantBatch[] = preview?.propositions || [];
  const propositionsFiltrees = useMemo(() => {
    let list = [...propositions];
    if (filtreCas) list = list.filter(p => p.cas === filtreCas);
    if (filtreGain) list = list.filter(p => p.gain > 0);
    return list.sort((a, b) => b.gain - a.gain);
  }, [propositions, filtreCas, filtreGain]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
      onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        {/* Header + tabs */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Calculator size={16} className="text-purple-600 dark:text-purple-400" />
            <h2 className="text-sm font-bold text-gray-900 dark:text-white">
              Rachat intelligent — par lot
            </h2>
            <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded p-0.5">
              <button onClick={() => setTab('config')}
                className={`px-2.5 py-1 text-[11px] font-medium rounded ${
                  tab === 'config'
                    ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                Configuration
              </button>
              <button onClick={() => setTab('historique')}
                className={`px-2.5 py-1 text-[11px] font-medium rounded flex items-center gap-1 ${
                  tab === 'historique'
                    ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                <History size={11} /> Historique
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-400">
            <X size={16} />
          </button>
        </div>

        {tab === 'config' ? (
          <ConfigView
            poles={poles || []} optionsFiltrees={optionsFiltrees} classes={classes || []}
            semestre={semestre} setSemestre={setSemestre}
            pole={pole} setPole={(v: string) => { setPole(v); setOption(''); }}
            niveau={niveau} setNiveau={setNiveau}
            option={option} setOption={setOption}
            classe={classe} setClasse={setClasse}
            moyMin={moyMin} setMoyMin={setMoyMin}
            moyMax={moyMax} setMoyMax={setMoyMax}
            casFiltre={casFiltre} setCasFiltre={setCasFiltre}
            plafond={plafond} setPlafond={setPlafond}
            propagerAuxDevoirs={propagerAuxDevoirs} setPropagerAuxDevoirs={setPropagerAuxDevoirs}
            autoriserSo={autoriserSo} setAutoriserSo={setAutoriserSo}
            autoriserSoZero={autoriserSoZero} setAutoriserSoZero={setAutoriserSoZero}
            nonRachetables={(preview?.non_rachetables || []) as NonRachetable[]}
            onManuel={(id: number) => setManuelPour(id)}
            onIntelligent={(id: number) => appliquerIds([id])}
            progression={progression}
            selection={selection} setSelection={setSelection}
            params={params}
            error={error} preview={preview} applied={applied}
            isLoading={isLoading}
            scopeActif={scopeActif}
            handlePreview={handlePreview}
            propositions={propositionsFiltrees} totalPropositions={propositions.length}
            filtreCas={filtreCas} setFiltreCas={setFiltreCas}
            filtreGain={filtreGain} setFiltreGain={setFiltreGain}
            confirmer={confirmer} setConfirmer={setConfirmer}
            handleAppliquer={handleAppliquer}
            onClose={onClose}
          />
        ) : (
          <HistoriqueView />
        )}
      </div>
      {manuelPour !== null && (
        <RachatManuelModal
          etudiantId={manuelPour}
          semestre={semestre}
          anneeAcademique={anneeAcademique}
          plafond={parseFloat(plafond) || 4}
          autoriserSo={autoriserSo}
          autoriserSoZero={autoriserSo && autoriserSoZero}
          propagerAuxDevoirs={propagerAuxDevoirs}
          onClose={() => setManuelPour(null)}
          onApplique={() => { retirerDeLaListe([manuelPour]); if (onSuccess) onSuccess(); }}
        />
      )}
    </div>
  );
};

// ── Vue Configuration ─────────────────────────────────────────────────

const ConfigView: React.FC<any> = ({
  poles, optionsFiltrees, classes,
  semestre, setSemestre, pole, setPole, niveau, setNiveau,
  option, setOption, classe, setClasse,
  moyMin, setMoyMin, moyMax, setMoyMax,
  casFiltre, setCasFiltre, plafond, setPlafond,
  propagerAuxDevoirs, setPropagerAuxDevoirs,
  autoriserSo, setAutoriserSo, autoriserSoZero, setAutoriserSoZero, nonRachetables, onManuel, onIntelligent, progression,
  selection, setSelection,
  params,
  error, preview, applied, isLoading, scopeActif, handlePreview,
  propositions, totalPropositions,
  filtreCas, setFiltreCas, filtreGain, setFiltreGain,
  confirmer, setConfirmer, handleAppliquer, onClose,
}) => (
  <>
    {/* Scope */}
    <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-purple-50/40 dark:bg-purple-900/10">
      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5 mb-2">
        <Filter size={13} /> Scope du rachat (au moins un filtre obligatoire)
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
        <Field label="Semestre *">
          <select value={semestre} onChange={(e) => setSemestre(Number(e.target.value) as 1 | 2)}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
            <option value={1}>S1</option>
            <option value={2}>S2</option>
          </select>
        </Field>
        <Field label="Pôle">
          <select value={pole} onChange={(e) => setPole(e.target.value)}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
            <option value="">—</option>
            {poles.map((p: any) => <option key={p.id} value={p.nom}>{p.nom}</option>)}
          </select>
        </Field>
        <Field label="Niveau">
          <select value={niveau} onChange={(e) => setNiveau(e.target.value)}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
            <option value="">—</option>
            {NIVEAUX.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>
        <Field label="Option">
          <select value={option} onChange={(e) => setOption(e.target.value)}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
            <option value="">—</option>
            {optionsFiltrees.map((o: any) => <option key={o.id} value={o.nom}>{o.nom}</option>)}
          </select>
        </Field>
        <Field label="Classe">
          <select value={classe} onChange={(e) => setClasse(e.target.value)}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
            <option value="">—</option>
            {classes.map((c: any) => <option key={c.id || c.code} value={c.code}>{c.code}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Field label="Tranche moy. min">
          <input type="number" step="0.01" min="0" max="20" value={moyMin} onChange={(e) => setMoyMin(e.target.value)}
            placeholder="ex. 6.50"
            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700" />
        </Field>
        <Field label="Tranche moy. max">
          <input type="number" step="0.01" min="0" max="20" value={moyMax} onChange={(e) => setMoyMax(e.target.value)}
            placeholder="ex. 9.99"
            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700" />
        </Field>
        <Field label="Type de cas">
          <select value={casFiltre} onChange={(e) => setCasFiltre(e.target.value as any)}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
            <option value="tous">Tous</option>
            <option value="sous_admission">Sous admission (moy &lt; 10)</option>
            <option value="eliminatoire">UE éliminatoire (moy ≥ 10)</option>
            <option value="mixte">Mixte (moy &lt; 10 et UE éliminatoire)</option>
          </select>
        </Field>
        <Field label={`Plafond (déf. ${params?.plafond_augmentation || '4.0'})`}>
          <input type="number" step="0.5" min="0.5" max="10" value={plafond} onChange={(e) => setPlafond(e.target.value)}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700" />
        </Field>
        <Field label="&nbsp;">
          <button onClick={handlePreview}
            disabled={!scopeActif || isLoading}
            className="w-full px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded font-medium flex items-center justify-center gap-1.5">
            {isLoading && !progression && <Loader size={12} className="animate-spin" />}
            Détecter les cas
          </button>
        </Field>
      </div>

      {/* Session ordinaire : dernier recours, décision explicite du jury */}
      <label className="mt-2 flex items-start gap-2 text-[11px] cursor-pointer text-gray-700 dark:text-gray-300">
        <input type="checkbox" checked={autoriserSo}
          onChange={(e) => setAutoriserSo(e.target.checked)}
          className="mt-0.5 rounded" />
        <div>
          <span className="font-semibold">Autoriser la session ordinaire en dernier recours</span>
          <span className="text-gray-500 dark:text-gray-400 ml-1">
            — seulement pour une matière sans rattrapage composé, et seulement une fois toutes les notes de rattrapage épuisées.
            <b className="text-amber-600 dark:text-amber-400 ml-1">Off par défaut.</b>
          </span>
        </div>
      </label>
      <label className={`mt-1 ml-6 flex items-start gap-2 text-[11px] ${autoriserSo ? 'cursor-pointer text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-600'}`}>
        <input type="checkbox" checked={autoriserSo && autoriserSoZero} disabled={!autoriserSo}
          onChange={(e) => setAutoriserSoZero(e.target.checked)}
          className="mt-0.5 rounded" />
        <div>
          <span className="font-semibold">Inclure les notes de session ordinaire à 0</span>
          <span className="text-gray-500 dark:text-gray-400 ml-1">
            — un 0 en session ordinaire est réputé non-participation : par défaut, seules les notes différentes de 0
            (preuve que l'étudiant a composé) peuvent être relevées.
            <b className="text-amber-600 dark:text-amber-400 ml-1">Off par défaut.</b>
          </span>
        </div>
      </label>

      {/* Option avancée : propager au devoir (OFF par défaut, à activer prudemment) */}
      <label className="mt-2 flex items-start gap-2 text-[11px] cursor-pointer text-gray-700 dark:text-gray-300">
        <input type="checkbox" checked={propagerAuxDevoirs}
          onChange={(e) => setPropagerAuxDevoirs(e.target.checked)}
          className="mt-0.5 rounded" />
        <div>
          <span className="font-semibold">Propager au devoir (D1 = D2 = nouvelle note)</span>
          <span className="text-gray-500 dark:text-gray-400 ml-1">
            — si SR (ou SO) == D1 == D2 (report examen → devoir), ou si l'étudiant n'a aucune note de devoir.
            Sans cette case, aucune note de devoir n'est jamais modifiée.
            <b className="text-amber-600 dark:text-amber-400 ml-1">Off par défaut</b> :
            les notes de devoir sont visibles des étudiants et collègues, un changement post-affichage peut prêter à confusion.
          </span>
        </div>
      </label>
    </div>

    {/* Règles */}
    <div className="px-5 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900/30 text-[11px] text-blue-900 dark:text-blue-200">
      <div className="flex items-start gap-2">
        <Info size={12} className="mt-0.5 flex-shrink-0" />
        <div>
          <b>Sous admission :</b> moy ∈ [8, 10[ sans UE éliminatoire → moy ≥ 10.
          <b className="ml-2">Éliminatoire :</b> moy ≥ 10 mais UE &lt; 6 → UE ≥ 6,01.
          <b className="ml-2">Mixte :</b> les deux à la fois.
          {' '}On relève <b>uniquement les notes de rattrapage</b>, peu à peu (pas de 0,25) sur plusieurs matières :
          d'abord les UE éliminatoires, puis l'UE la plus faible, et dans l'UE la matière la plus faible.
          Une SR sous la SO peut passer au-dessus d'elle ; le plafond se compte depuis la note retenue (la meilleure des deux).
          La moyenne « après » est celle du <b>calcul officiel</b>.
        </div>
      </div>
    </div>

    {/* Body */}
    <div className="flex-1 overflow-y-auto p-4">
      {error && (
        <div className="flex items-start gap-2 p-3 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}
      {applied && (
        <div className="flex items-start gap-2 p-3 mb-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm text-green-700 dark:text-green-300">
          <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-bold">{applied.nb_etudiants_traites} étudiant(s) rachetés.</div>
            <div className="text-xs">
              {applied.nb_notes_examen_modifiees} notes d'examen + {applied.nb_notes_devoir_modifiees} notes de devoir mises à jour.
              {applied.batch_id && <>{' '}Batch ID : <code className="text-[10px]">{applied.batch_id}</code></>}
            </div>
            {applied.appliques && applied.appliques.length > 0 && (
              <div className="mt-2 overflow-x-auto">
                <table className="min-w-full text-[11px] text-gray-700 dark:text-gray-300">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="pr-3 py-0.5">Matricule</th>
                      <th className="pr-3 py-0.5">Classe</th>
                      <th className="pr-3 py-0.5 text-right">Avant</th>
                      <th className="pr-3 py-0.5 text-right">Après (recalculée)</th>
                      <th className="pr-3 py-0.5 text-right">Simulée</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applied.appliques.map((a: any) => (
                      <tr key={a.etudiant_id}>
                        <td className="pr-3 py-0.5 font-mono">{a.matricule}</td>
                        <td className="pr-3 py-0.5">{a.classe}</td>
                        <td className="pr-3 py-0.5 text-right">{a.moy_avant?.toFixed(2) ?? '—'}</td>
                        <td className="pr-3 py-0.5 text-right font-semibold">{a.moy_apres?.toFixed(2) ?? '—'}</td>
                        <td className="pr-3 py-0.5 text-right text-gray-500">{a.moy_apres_simule?.toFixed(2) ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="text-[10px] text-gray-500 mt-1">
                  La moyenne « après » est celle du calcul officiel de la délibération ; c'est elle qui figure au PV.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {applied && applied.echecs && applied.echecs.length > 0 && (
        <div className="flex items-start gap-2 p-3 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-bold">
              {applied.echecs.length} étudiant(s) non rachetés : aucune note modifiée pour eux.
            </div>
            <ul className="mt-1 space-y-0.5 text-xs">
              {applied.echecs.map((e: any) => (
                <li key={e.etudiant_id}>
                  <span className="font-mono">{e.matricule}</span> {e.nom} ({e.classe}) — {e.motif}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {preview && !applied && (preview.nb_moyennes_rafraichies ?? 0) > 0 && (
        <div className="flex items-start gap-2 p-3 mb-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-800 dark:text-amber-300">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <b>{preview.nb_moyennes_rafraichies} moyenne(s) du tableau de délibération n'étaient pas à jour</b> par
            rapport aux notes actuelles. La simulation part des moyennes recalculées (rien n'a été enregistré).
            Relancez le calcul de la délibération pour que le tableau affiche les mêmes valeurs.
          </div>
        </div>
      )}
      {!preview && !error && !applied && (
        <div className="text-center text-sm text-gray-500 py-12">
          Configurez votre scope puis cliquez sur <b>Prévisualiser</b> pour voir
          la liste des étudiants rachetables et le gain simulé.
        </div>
      )}

      {preview && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
            <Kpi label="Scope" value={preview.total_scope} couleur="text-gray-700 dark:text-gray-300" />
            <Kpi label="Rachetables" value={preview.total_eligibles} couleur="text-green-600 dark:text-green-400" />
            <Kpi label="Sous adm." value={propositions.filter((p: any) => p.cas === 'sous_admission').length} couleur="text-blue-600 dark:text-blue-400" />
            <Kpi label="Éliminatoire" value={propositions.filter((p: any) => p.cas === 'eliminatoire').length} couleur="text-orange-600 dark:text-orange-400" />
            <Kpi label="Mixte" value={propositions.filter((p: any) => p.cas === 'mixte').length} couleur="text-rose-600 dark:text-rose-400" />
            <Kpi label="Non rachetables" value={nonRachetables.length} couleur="text-gray-500" />
          </div>
          {(
            <div className="mb-3 p-3 rounded-lg border border-purple-200 dark:border-purple-900/50 bg-purple-50/50 dark:bg-purple-900/10 text-xs text-gray-700 dark:text-gray-300">
              <div className="font-semibold text-gray-900 dark:text-white mb-1">Cas détectés</div>
              <div>
                <b>{preview.total_eligibles}</b> étudiant(s) rachetable(s)
                {' '}({propositions.filter((p: any) => !p.utilise_so).length} avec le rattrapage seul
                {propositions.some((p: any) => p.utilise_so) && <>, {propositions.filter((p: any) => p.utilise_so).length} en touchant la session ordinaire</>}).
                {' '}<b>{nonRachetables.length}</b> cas non rachetable(s) dans les règles
                {nonRachetables.some((n: NonRachetable) => n.possible_avec_so) && !autoriserSo && (
                  <>, dont <b>{nonRachetables.filter((n: NonRachetable) => n.possible_avec_so).length}</b> rachetable(s)
                  si vous autorisez la session ordinaire</>
                )}.
              </div>
              <div className="mt-1 text-gray-500">
                Pour chaque étudiant : <b>Intelligent</b> applique la proposition vérifiée, <b>Manuel</b> ouvre ses notes pour
                les ajuster et simuler avant d'appliquer. En masse : cochez puis appliquez en bas de la fenêtre.
              </div>
            </div>
          )}
          {progression && (
            <div className="mb-3 p-2 rounded border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 text-xs text-blue-800 dark:text-blue-300 flex items-center gap-2">
              <Loader size={12} className="animate-spin" />
              Application en cours : {progression.fait}/{progression.total} étudiant(s) traités (vérification par le calcul officiel)…
            </div>
          )}

          {/* Filtres affichage */}
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              Propositions ({propositions.length}/{totalPropositions})
            </h3>
            <div className="flex items-center gap-3 text-[11px]">
              <select value={filtreCas} onChange={(e) => setFiltreCas(e.target.value)}
                className="px-1.5 py-0.5 text-[11px] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
                <option value="">Tous les cas</option>
                <option value="sous_admission">Sous admission</option>
                <option value="eliminatoire">Éliminatoire</option>
                <option value="mixte">Mixte</option>
              </select>
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={filtreGain} onChange={(e) => setFiltreGain(e.target.checked)} />
                Avec gain &gt; 0 uniquement
              </label>
            </div>
          </div>

          {/* Table */}
          {propositions.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-10">
              {totalPropositions === 0
                ? 'Aucun étudiant éligible dans ce scope.'
                : 'Aucun résultat ne correspond aux filtres d\'affichage.'}
            </div>
          ) : (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-700/50 text-[10px] uppercase tracking-wide text-gray-500 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 w-8 text-center">
                      <input type="checkbox"
                        ref={(el) => {
                          if (!el) return;
                          const visibleIds = propositions.map((p: any) => p.etudiant_id);
                          const checkedCount = visibleIds.filter((id: number) => selection.has(id)).length;
                          el.indeterminate = checkedCount > 0 && checkedCount < visibleIds.length;
                        }}
                        checked={propositions.length > 0 && propositions.every((p: any) => selection.has(p.etudiant_id))}
                        onChange={(e) => {
                          const next = new Set(selection);
                          if (e.target.checked) propositions.forEach((p: any) => next.add(p.etudiant_id));
                          else propositions.forEach((p: any) => next.delete(p.etudiant_id));
                          setSelection(next);
                        }}
                        title="Tout cocher / décocher" />
                    </th>
                    <th className="px-2 py-2 text-left">Matricule</th>
                    <th className="px-2 py-2 text-left">Nom</th>
                    <th className="px-2 py-2 text-left">Classe</th>
                    <th className="px-2 py-2 text-center">Cas</th>
                    <th className="px-2 py-2 text-right">Moy avant</th>
                    <th className="px-2 py-2 text-right">Moy après</th>
                    <th className="px-2 py-2 text-right">Gain</th>
                    <th className="px-2 py-2 text-center" title="UEs validées avant → après / total">UEs</th>
                    <th className="px-2 py-2 text-center">Notes</th>
                    <th className="px-2 py-2 text-center">UEs traitées</th>
                    <th className="px-2 py-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {propositions.map((p: PropositionEtudiantBatch) => (
                    <PropLine key={p.etudiant_id} p={p}
                      onManuel={() => onManuel(p.etudiant_id)}
                      onIntelligent={() => onIntelligent(p.etudiant_id)}
                      occupe={isLoading}
                      selectionne={selection.has(p.etudiant_id)}
                      onToggle={() => {
                        const next = new Set(selection);
                        if (next.has(p.etudiant_id)) next.delete(p.etudiant_id);
                        else next.add(p.etudiant_id);
                        setSelection(next);
                      }} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {nonRachetables.length > 0 && (
            <div className="mt-4">
              <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Cas non rachetables dans les règles ({nonRachetables.length})
              </h3>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 text-[10px] uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-2 py-2 text-left">Matricule</th>
                      <th className="px-2 py-2 text-left">Nom</th>
                      <th className="px-2 py-2 text-left">Classe</th>
                      <th className="px-2 py-2 text-center">Cas</th>
                      <th className="px-2 py-2 text-right">Moy</th>
                      <th className="px-2 py-2 text-right" title="Toutes les notes relevables au plafond">Max atteignable</th>
                      <th className="px-2 py-2 text-left">Motif</th>
                      <th className="px-2 py-2 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {nonRachetables.map((n: NonRachetable) => (
                      <tr key={n.etudiant_id}>
                        <td className="px-2 py-1.5 font-mono text-[11px]">{n.matricule}</td>
                        <td className="px-2 py-1.5">{n.nom}</td>
                        <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">{n.classe}</td>
                        <td className="px-2 py-1.5 text-center">{LIB_CAS[n.cas] || n.cas}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{n.moy_avant?.toFixed(2) ?? '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{n.moy_max_atteignable?.toFixed(2) ?? '—'}</td>
                        <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">
                          {n.motif}
                          {n.possible_avec_so && (
                            <span className="ml-1 px-1 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-[10px]">
                              possible avec la session ordinaire
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <button onClick={() => onManuel(n.etudiant_id)}
                            className="px-2 py-0.5 text-[10px] rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                            Manuel
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>

    {/* Footer */}
    {preview && preview.total_eligibles > 0 && (
      <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
          <input type="checkbox" checked={confirmer} onChange={(e) => setConfirmer(e.target.checked)} className="rounded" />
          Je confirme — appliquer le rachat aux <b>{selection.size}</b> étudiant(s) cochés
          <span className="text-gray-400">(sur {preview.total_eligibles} éligibles)</span>
        </label>
        <div className="flex gap-2">
          <button onClick={onClose}
            className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            Annuler
          </button>
          <button onClick={handleAppliquer}
            disabled={!confirmer || isLoading || selection.size === 0}
            className="px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded font-medium flex items-center gap-1.5">
            {isLoading && <Loader size={12} className="animate-spin" />}
            Rachat intelligent des {selection.size} coché(s)
          </button>
        </div>
      </div>
    )}
    {applied && !(preview && preview.total_eligibles > 0) && (
      <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex justify-end">
        <button onClick={onClose}
          className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">
          Fermer
        </button>
      </div>
    )}
  </>
);

const PropLine: React.FC<{
  p: PropositionEtudiantBatch;
  selectionne: boolean;
  onToggle: () => void;
  onManuel: () => void;
  onIntelligent: () => void;
  occupe: boolean;
}> = ({ p, selectionne, onToggle, onManuel, onIntelligent, occupe }) => {
  const [open, setOpen] = useState(false);
  const gainUes = p.nb_ues_validees_apres - p.nb_ues_validees_avant;
  return (
    <>
      <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
        p.cas === 'eliminatoire' ? 'bg-orange-50/40 dark:bg-orange-900/10' : ''
      } ${!selectionne ? 'opacity-50' : ''}`}>
        <td className="px-2 py-1.5 text-center">
          <input type="checkbox" checked={selectionne} onChange={onToggle}
            title="Cocher pour appliquer le rachat à cet étudiant" />
        </td>
        <td className="px-2 py-1.5 font-mono text-[11px]">{p.matricule}</td>
        <td className="px-2 py-1.5 font-medium text-gray-900 dark:text-white">{p.nom}</td>
        <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">{p.classe}</td>
        <td className="px-2 py-1.5 text-center">
          <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${
            p.cas === 'eliminatoire'
              ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
              : p.cas === 'mixte'
                ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300'
                : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
          }`}>
            {LIB_CAS[p.cas] || p.cas}
          </span>
          {p.utilise_so && (
            <span className="ml-1 inline-block px-1 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
              title="Le plan touche la session ordinaire (dernier recours)">SO</span>
          )}
        </td>
        <td className="px-2 py-1.5 text-right tabular-nums">{p.moy_avant.toFixed(2)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums font-bold text-green-600 dark:text-green-400">
          {p.moy_apres_simule.toFixed(2)}
        </td>
        <td className="px-2 py-1.5 text-right tabular-nums font-bold">
          <span className={p.gain > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
            +{p.gain.toFixed(2)}
          </span>
        </td>
        <td className="px-2 py-1.5 text-center text-[10px] tabular-nums">
          <div>
            <span className="text-gray-500">{p.nb_ues_validees_avant}</span>
            <span className="text-gray-400 mx-0.5">→</span>
            <span className="font-bold text-green-600 dark:text-green-400">{p.nb_ues_validees_apres}</span>
            <span className="text-gray-400">/{p.nb_ues_total}</span>
          </div>
          {gainUes > 0 && (
            <div className="text-[9px] text-green-600 font-bold">+{gainUes} UE</div>
          )}
        </td>
        <td className="px-2 py-1.5 text-center">
          <button onClick={() => setOpen(!open)}
            className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mx-auto">
            <ChevronRight size={10} className={open ? 'rotate-90 transition-transform' : 'transition-transform'} />
            {p.notes_modifiees.length}
          </button>
        </td>
        <td className="px-2 py-1.5 text-center text-[10px] text-gray-500">
          {p.ues_traitees.map(u => u.code_ue).join(', ')}
        </td>
        <td className="px-2 py-1.5 text-center whitespace-nowrap">
          <button onClick={onIntelligent} disabled={occupe}
            className="px-2 py-0.5 text-[10px] rounded bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-40 mr-1"
            title="Appliquer la proposition vérifiée à cet étudiant">
            Intelligent
          </button>
          <button onClick={onManuel} disabled={occupe}
            className="px-2 py-0.5 text-[10px] rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
            title="Ajuster les notes, simuler, puis appliquer">
            Manuel
          </button>
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50/50 dark:bg-gray-700/30">
          <td colSpan={12} className="px-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase text-gray-500 mb-1">Notes modifiées</div>
                <table className="w-full text-[10px]">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="px-1.5 py-0.5 text-left">Matière</th>
                      <th className="px-1.5 py-0.5 text-left">UE</th>
                      <th className="px-1.5 py-0.5 text-center">Source</th>
                      <th className="px-1.5 py-0.5 text-right">Avant</th>
                      <th className="px-1.5 py-0.5 text-right">Après</th>
                      <th className="px-1.5 py-0.5 text-right" title="Note d'examen retenue avant rachat : la meilleure de SO et SR">Retenue avant</th>
                      <th className="px-1.5 py-0.5 text-right" title="Hausse de la note retenue">Gain réel</th>
                      <th className="px-1.5 py-0.5 text-center">+ Dev.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.notes_modifiees.map((n, i) => {
                      const retenue = Math.max(n.note_avant, n.source === 'SR' ? (n.so_val ?? 0) : 0);
                      return (
                      <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                        <td className="px-1.5 py-0.5" title={n.matiere_code}>{n.matiere_nom}</td>
                        <td className="px-1.5 py-0.5 font-mono text-gray-500">{n.ue_code}</td>
                        <td className="px-1.5 py-0.5 text-center">
                          <span className={`px-1 rounded text-[9px] font-bold ${
                            n.source === 'SR'
                              ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
                              : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                          }`}>{n.source}</span>
                        </td>
                        <td className="px-1.5 py-0.5 text-right tabular-nums">{n.note_avant.toFixed(2)}</td>
                        <td className="px-1.5 py-0.5 text-right tabular-nums font-bold">{n.note_apres.toFixed(2)}</td>
                        <td className="px-1.5 py-0.5 text-right tabular-nums text-gray-500">{retenue.toFixed(2)}</td>
                        <td className="px-1.5 py-0.5 text-right tabular-nums">+{(n.note_apres - retenue).toFixed(2)}</td>
                        <td className="px-1.5 py-0.5 text-center">
                          {n.devoir_conjoint && <span className="text-amber-600 text-[10px]">oui</span>}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase text-gray-500 mb-1">UEs traitées</div>
                <table className="w-full text-[10px]">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="px-1.5 py-0.5 text-left">UE</th>
                      <th className="px-1.5 py-0.5 text-right">Avant</th>
                      <th className="px-1.5 py-0.5 text-right">Après</th>
                      <th className="px-1.5 py-0.5 text-center">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.ues_traitees.map((u, i) => (
                      <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                        <td className="px-1.5 py-0.5 font-mono">{u.code_ue}</td>
                        <td className="px-1.5 py-0.5 text-right tabular-nums">{u.moy_avant.toFixed(2)}</td>
                        <td className="px-1.5 py-0.5 text-right tabular-nums font-bold">{u.moy_apres.toFixed(2)}</td>
                        <td className="px-1.5 py-0.5 text-center">
                          {u.devient_validee && <span className="px-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded text-[9px]">Validée</span>}
                          {!u.devient_validee && u.etait_eliminatoire && u.moy_apres >= 6.01 && (
                            <span className="px-1 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded text-[9px]">Compensable</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ── Vue Historique ───────────────────────────────────────────────────

const HistoriqueView: React.FC = () => {
  const { data, isLoading, refetch } = useGetHistoriqueBatchesQuery();
  const [annuler] = useAnnulerBatchRachatMutation();
  const [trigger, { data: details, isFetching: detailsLoading }] = useLazyGetBatchDetailsQuery();
  const [batchOpen, setBatchOpen] = useState<string | null>(null);
  const [confirmAnnuler, setConfirmAnnuler] = useState<string | null>(null);
  const [resultAnnuler, setResultAnnuler] = useState<string | null>(null);

  const openDetails = (batchId: string) => {
    setBatchOpen(batchId);
    trigger({ batch_id: batchId });
  };

  const handleAnnuler = async (batchId: string) => {
    setResultAnnuler(null);
    try {
      const res = await annuler({ batch_id: batchId }).unwrap();
      const nr = res.non_restaurees || [];
      setResultAnnuler(`Lot annulé : ${res.nb_notes_restaurees} note(s) restaurée(s), ${res.nb_etudiants_recalcules} étudiant(s) recalculé(s).`
        + (nr.length
          ? ` Attention : ${nr.length} note(s) introuvable(s) sous leur semestre, laissée(s) telle(s) quelle(s) — à vérifier : `
            + nr.map((n) => `${n.matricule} ${n.matiere} (${n.source} ${n.note_apres} → ${n.note_avant})`).join(' ; ')
          : ''));
      setConfirmAnnuler(null);
      refetch();
    } catch (e: any) {
      setResultAnnuler(`Erreur : ${e?.data?.error || e?.message || 'inconnu'}`);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-gray-500">
          {data ? `${data.total} batch(es) trouvé(s)` : ''}
        </div>
        <button onClick={() => refetch()}
          className="px-2 py-1 text-[10px] bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 flex items-center gap-1">
          <RefreshCw size={10} /> Actualiser
        </button>
      </div>

      {resultAnnuler && (
        <div className="p-3 mb-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-700 dark:text-blue-300">
          {resultAnnuler}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-10"><Loader className="animate-spin mx-auto" /></div>
      ) : (data?.batches || []).length === 0 ? (
        <div className="text-center text-sm text-gray-500 py-10">Aucun batch dans l'historique.</div>
      ) : (
        <div className="space-y-2">
          {data?.batches.map(b => (
            <div key={b.batch_id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/30 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-3 text-xs">
                  <code className="text-[10px] text-gray-500">{b.batch_id.slice(0, 8)}…</code>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    b.statut === 'annule'
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                  }`}>
                    {b.statut === 'annule' ? 'Annulé' : 'Appliqué'}
                  </span>
                  <span className="text-gray-700 dark:text-gray-300">
                    <b>{b.nb_etudiants}</b> étudiant(s) — <b>{b.nb_modifications}</b> note(s)
                  </span>
                  {b.cas && (
                    <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-[10px]">
                      {b.cas === 'eliminatoire' ? 'Éliminatoire' : b.cas === 'mixte' ? 'Mixte'
                        : b.cas === 'manuel' ? 'Manuel' : 'Sous admission'}
                    </span>
                  )}
                  <span className="text-[10px] text-gray-500">
                    {b.date_debut ? new Date(b.date_debut).toLocaleString('fr-FR') : '—'}
                  </span>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => openDetails(b.batch_id)}
                    className="px-2 py-1 text-[10px] bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 hover:bg-blue-200 rounded">
                    {batchOpen === b.batch_id ? 'Masquer' : 'Détails'}
                  </button>
                  {b.statut !== 'annule' && (
                    <>
                      {confirmAnnuler === b.batch_id ? (
                        <>
                          <button onClick={() => handleAnnuler(b.batch_id)}
                            className="px-2 py-1 text-[10px] bg-red-600 text-white rounded font-bold">
                            Confirmer annulation
                          </button>
                          <button onClick={() => setConfirmAnnuler(null)}
                            className="px-2 py-1 text-[10px] border border-gray-300 dark:border-gray-600 rounded">
                            Non
                          </button>
                        </>
                      ) : (
                        <button onClick={() => setConfirmAnnuler(b.batch_id)}
                          className="px-2 py-1 text-[10px] bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 hover:bg-red-200 rounded flex items-center gap-1">
                          <Trash2 size={10} /> Annuler
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {batchOpen === b.batch_id && (
                <div className="px-3 py-2">
                  {detailsLoading ? (
                    <Loader className="animate-spin mx-auto" />
                  ) : details && details.batch_id === b.batch_id ? (
                    <div className="text-xs">
                      <div className="mb-2 text-gray-600 dark:text-gray-400">
                        {details.total_etudiants} étudiant(s), {details.total_modifications} modification(s).
                      </div>
                      <table className="w-full text-[11px]">
                        <thead className="bg-gray-100 dark:bg-gray-700/50 text-[10px] uppercase">
                          <tr>
                            <th className="px-2 py-1 text-left">Matricule</th>
                            <th className="px-2 py-1 text-left">Nom</th>
                            <th className="px-2 py-1 text-left">Classe</th>
                            <th className="px-2 py-1 text-right">Nb mods</th>
                            <th className="px-2 py-1 text-left">Notes touchées</th>
                          </tr>
                        </thead>
                        <tbody>
                          {details.etudiants.map(et => (
                            <tr key={et.etudiant_id} className="border-t border-gray-100 dark:border-gray-700">
                              <td className="px-2 py-1 font-mono">{et.matricule}</td>
                              <td className="px-2 py-1">{et.nom}</td>
                              <td className="px-2 py-1 text-gray-600">{et.classe}</td>
                              <td className="px-2 py-1 text-right">{et.modifications.length}</td>
                              <td className="px-2 py-1 text-[10px] text-gray-500">
                                {et.modifications.map((m, i) => (
                                  <span key={i} className="inline-block mr-1.5">
                                    <b>{m.matiere_code}</b>
                                    <span className="text-gray-400"> {m.source}: {m.note_avant?.toFixed(2)} → {m.note_apres?.toFixed(2)}</span>
                                  </span>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-semibold uppercase text-gray-500 mb-0.5">
      <span dangerouslySetInnerHTML={{ __html: label }} />
    </label>
    {children}
  </div>
);

const Kpi: React.FC<{ label: string; value: any; couleur: string }> = ({ label, value, couleur }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2 text-center">
    <div className={`text-lg font-bold tabular-nums ${couleur}`}>{value}</div>
    <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold mt-0.5">{label}</div>
  </div>
);

export default RachatIntelligentBatchModal;
